import { useCallback, useEffect, useMemo, useState } from 'react';

import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import CloseOutlined from '@mui/icons-material/CloseOutlined';
import { Box } from '@mui/material';
import { useParams } from 'react-router';

import GroupedSelect from '@/components/GroupedSelect';
import type { Option } from '@/components/GroupedSelect/types';
import { useUpdateDashboard } from '@/features/Dashboard/api';
import { SAVE_SNACKBAR_KEY } from '@/features/Dashboard/api/mutations';
import Heading from '@/features/Dashboard/components/Heading';
import { Preview } from '@/features/Dashboard/components/Preview';
import { HIGHLIGHTED_PRICE_MAX_ITEMS } from '@/features/Dashboard/constants/const';
import { useWidgetAnimation } from '@/features/Dashboard/context/WidgetAnimation/hooks';
import { DrawerFooterButtons } from '@/features/Dashboard/drawer/components/DrawerFooterButtons';
import { useDrawerSteps } from '@/features/Dashboard/drawer/context/DrawerStepsContext';
import { DrawerBody } from '@/features/Dashboard/drawer/DrawerBody';
import { DrawerButton } from '@/features/Dashboard/drawer/DrawerButton';
import { DrawerHeader } from '@/features/Dashboard/drawer/DrawerHeader';
import { useBuildDashboardStructure } from '@/features/Dashboard/hooks/useBuildDashboardStructure';
import { getWidgetAnimationIds } from '@/features/Dashboard/hooks/useBuildDashboardStructure.utils';
import { useCurrentDashboardTabConfig } from '@/features/Dashboard/hooks/useCurrentDashboardConfig';
import { useWidgetOptions } from '@/features/Dashboard/hooks/useWidgetOptions';
import { useDashboardEditActions } from '@/features/Dashboard/pages/Edit/hooks/useDashboardEditActions';
import {
  selectCurrentTabId,
  selectCurrentTiles,
  selectInitialDashboardName
} from '@/features/Dashboard/pages/Edit/model/dashboardEdit.selectors';
import getWidgetType from '@/features/Dashboard/utils/getWidgetType';
import { useMarketWidgets } from '@/features/Markets/api';
import { useAppSelector } from '@/hooks/redux-hooks';
import useSnackbarNotifications from '@/hooks/useSnackbarNotifications';

import {
  buildImportedAppFromWidget,
  buildSuccessMessage,
  computeNextApps,
  handleEditModeImport
} from './utils';

import type { SelectableWidget } from './utils';

const groupedSelectSx = {
  justifyContent: 'space-between',
  '& > .label__container': {
    width: 'auto'
  }
} as const;

export const ImportMarketWidget = () => {
  const [select, setSelect] = useState<{
    market: string | null;
    tab: string | null;
    widget: string | null;
  }>({
    market: null,
    tab: null,
    widget: null
  });

  const groupedMarkets = useAppSelector(state => state.marketsList.groupedMarkets);

  const marketsOptions = useMemo(
    () =>
      groupedMarkets
        .map(group => {
          return {
            label: group.name,
            options: group.markets
              .filter(m => !m.comingSoon)
              .map(market => ({
                label: market.name,
                value: market.marketId as string
              }))
          };
        })
        .filter(group => group.options.length > 0),
    [groupedMarkets]
  );

  const { data, isLoading } = useMarketWidgets(select.market);

  const tabOptions = useMemo(() => {
    if (!data) return [];
    return data.map(tab => ({
      label: tab.name,
      value: tab.name
    }));
  }, [data]);

  const selectedTab = useMemo(() => {
    if (!data) return;
    return data.find(tab => tab.name === select.tab);
  }, [data, select.tab]);

  const widgetOptions = useWidgetOptions(selectedTab);

  const handleMarketChange = ({ value }: Option) => {
    if (isPending) return;
    setSelect(() => ({ market: value, tab: null, widget: null }));
    setHasChanges(true);
  };

  const handleTabChange = ({ value }: Option) => {
    if (isPending) return;
    setSelect(prev => ({ ...prev, tab: value, widget: null }));
    setHasChanges(true);
  };

  const handleWidgetChange = ({ value }: Option) => {
    if (isPending) return;
    setSelect(prev => ({ ...prev, widget: value }));
    setHasChanges(true);
  };

  const selectedWidget = useMemo(() => {
    if (!selectedTab || !select.widget) return null;

    return (
      selectedTab.widgets.find(w => `${w.name}-{${getWidgetType(w)}}` === select.widget) || null
    );
  }, [selectedTab, select.widget]);
  const initialDashboardNameFromRedux = useAppSelector(selectInitialDashboardName);
  const currentTabId = useAppSelector(selectCurrentTabId);
  const currentTiles = useAppSelector(selectCurrentTiles);
  const params = useParams<{ dashboardId: string; tabName: string }>();

  const dashboardName =
    initialDashboardNameFromRedux ||
    (params.dashboardId ? decodeURIComponent(params.dashboardId) : '');
  const currentTabName = params.tabName ? decodeURIComponent(params.tabName) : undefined;

  const { mutate, isPending } = useUpdateDashboard(
    dashboardName,
    '', // Success message handled manually in handleSave
    currentTabName
  );

  const {
    closeDrawer,
    setHasChanges,
    setPreventClose,
    handleAttemptBack,
    isEditMode,
    editingTarget
  } = useDrawerSteps();
  const actions = useDashboardEditActions();
  const { showErrorSnackbar, showSuccessSnackbar } = useSnackbarNotifications();

  useEffect(() => {
    // Block drawer closing on outside click while update is pending
    setPreventClose(isPending);
  }, [isPending, setPreventClose]);

  const { setElementsToAnimateId } = useWidgetAnimation();
  const dashboardConfig = useCurrentDashboardTabConfig();

  const templateWidget = useMemo(() => {
    if (!selectedWidget) return;

    return buildImportedAppFromWidget(selectedWidget as SelectableWidget, dashboardConfig || []);
  }, [dashboardConfig, selectedWidget]);

  const buildDashboardStructure = useBuildDashboardStructure();

  const templateWidgetWithoutId = useMemo(() => {
    if (!templateWidget) return;
    if ('type' in templateWidget) {
      return { ...templateWidget, tiles: templateWidget.tiles.map(tile => ({ ...tile, id: '' })) };
    }
    return { ...templateWidget, id: '' };
  }, [templateWidget]);

  const handleSave = useCallback(() => {
    if (!templateWidget) return;

    setElementsToAnimateId(getWidgetAnimationIds(templateWidget));

    if (isEditMode) {
      if (!currentTabId) return;
      handleEditModeImport({
        actions,
        currentTabId,
        currentTiles,
        templateWidget,
        editingTarget,
        showErrorSnackbar,
        showSuccessSnackbar
      });
      closeDrawer();
      return;
    }

    if (isPending) return;

    const result = computeNextApps(dashboardConfig, templateWidget);

    if (result.exceeded) {
      const tilesLabel = `tile${result.limitedCount === 1 ? '' : 's'}`;
      showErrorSnackbar(
        `Tile limit of ${HIGHLIGHTED_PRICE_MAX_ITEMS} exceeded. Only ${result.limitedCount} ${tilesLabel} added: ${result.addedCodes.join(', ')}`,
        5000,
        'tiles-limit-exceeded-error'
      );
    }

    mutate(buildDashboardStructure(templateWidget), {
      onSuccess: () => {
        showSuccessSnackbar(buildSuccessMessage(result), SAVE_SNACKBAR_KEY);
        closeDrawer();
      }
    });
  }, [
    actions,
    buildDashboardStructure,
    closeDrawer,
    currentTabId,
    currentTiles,
    dashboardConfig,
    editingTarget,
    isEditMode,
    isPending,
    mutate,
    setElementsToAnimateId,
    showErrorSnackbar,
    showSuccessSnackbar,
    templateWidget
  ]);

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      <DrawerHeader
        before={
          <DrawerButton
            disabled={isPending}
            icon={<ArrowBackIcon />}
            title='Go back'
            variant='secondary'
            onClick={handleAttemptBack}
          />
        }
        buttons={
          <DrawerButton
            disabled={isPending}
            icon={<CloseOutlined />}
            title='Discard'
            variant='secondary'
            onClick={() => {
              if (!isPending) closeDrawer();
            }}
          />
        }
        title='Add new app'
      />
      <DrawerBody>
        <Heading
          subtitle='Select your market widget'
          title='Configure import market widget'
        />
        <Box
          sx={{
            mt: 3,
            display: 'flex',
            flexDirection: 'column',
            gap: 3,
            '.label__container': {
              fontWeight: 700,
              fontSize: '14px'
            }
          }}
        >
          <GroupedSelect
            disabled={isPending}
            label='Select market'
            options={marketsOptions}
            placeholder='Select market'
            sx={groupedSelectSx}
            value={select.market}
            onChange={handleMarketChange}
          />
          <GroupedSelect
            disabled={isPending || !select.market || tabOptions.length === 0}
            isLoading={isLoading}
            label='Select page'
            options={tabOptions}
            placeholder='Select page'
            sx={groupedSelectSx}
            value={select.tab}
            onChange={handleTabChange}
          />
          <GroupedSelect
            disabled={isPending || !select.tab}
            isLoading={isLoading}
            label='Select widget'
            options={widgetOptions}
            placeholder='Select widget'
            sx={groupedSelectSx}
            value={select.widget}
            onChange={handleWidgetChange}
          />
          <Preview
            enabled={!!templateWidgetWithoutId}
            template={templateWidgetWithoutId}
          />
        </Box>
      </DrawerBody>
      <Box sx={{ mt: 'auto' }}>
        <DrawerFooterButtons
          disabled={!selectedWidget || isPending}
          onDiscard={closeDrawer}
          onSave={handleSave}
        />
      </Box>
    </Box>
  );
};
